import { DependentEventFilter, EventFilter } from '@fullstory/proto/fs/api/segment';
import { useDebounce } from '@fullstory/react-hooks';
import React, { useCallback, useContext, useMemo, useState } from 'react';
import { SidebarElementPicker } from '../components/SidebarElementPicker';
import {
  SignalStudioContext,
  usePreviewSearch,
  AppearancesContextWithOnPreview,
  OnPreviewFnWithIndex,
  SignalStudioComponentProps,
  SignalStudioHighlight,
} from '@fullstory/signal-studio-context';
import { useIntl } from 'react-intl';

export interface useSidebarElementPickerProps {
  initialSelector: string;
  onConfirm: (selector: string) => void;
}

export const useElementPickerSignalStudio = (
  props: useSidebarElementPickerProps,
): {
  SignalStudio: React.ComponentType<SignalStudioComponentProps> | null;
  signalStudioProps: Omit<SignalStudioComponentProps, 'header' | 'onCloseSignal'>;
} => {
  const intl = useIntl();
  const SignalStudio = useContext(SignalStudioContext);

  // the selector that the user chooses either by editing directly or through preview
  const [selector, setSelector] = useState<string>(props.initialSelector);
  // the selector that the user chooses, not updated by preview updates
  const [selectorForPreviewDep, setSelectorForPreviewDep] = useState<string>(props.initialSelector);

  // because the selectors are often user typing, we debounce them for preview updates
  const debouncedSelectorForPreviewDep = useDebounce(selectorForPreviewDep, 800);

  const eventFiltersPreview = useMemo(
    (): (EventFilter | null)[] => [
      // prefer click
      { click: { any: {} } },
      // but fall back to any activity if no click data is available
      null,
    ],
    [],
  );

  // for deps, the preview deps only update when the user explicitly updates the input (to avoid preview thrash while inspecting)
  // these deps are only used if we need them as a fallback
  const fallbackDepsPreview = useMemo(
    (): DependentEventFilter[] =>
      debouncedSelectorForPreviewDep
        ? [{ css: { matches: { value: [debouncedSelectorForPreviewDep] } } }]
        : [],
    [debouncedSelectorForPreviewDep],
  );

  const highlights = useMemo(
    (): SignalStudioHighlight[] =>
      selector ? [{ type: 'inspected', target: { selector, platforms: [] } }] : [],
    [selector],
  );

  const previewSearches = usePreviewSearch(eventFiltersPreview, [fallbackDepsPreview]);
  const onPreview: OnPreviewFnWithIndex = useCallback(
    (primary, refinements, _, index) => {
      previewSearches[index]?.replace?.(primary, refinements, true);
    },
    [previewSearches],
  );

  const ConfigSidebar = (
    <AppearancesContextWithOnPreview onPreview={onPreview}>
      <SidebarElementPicker
        selector={selector}
        onChange={selector => {
          setSelector(selector);
          setSelectorForPreviewDep(selector); // explicit changes through the sidebar will trigger new previews when applicable
        }}
      />
    </AppearancesContextWithOnPreview>
  );

  return {
    SignalStudio,
    signalStudioProps: {
      lowerConfigPartName: intl.formatMessage({ defaultMessage: 'selector', id: 'A7ml5w' }),
      configSidebar: ConfigSidebar,
      preview: {
        index: 0,
        onIndexChange: () => {},
        mode: 'element-inspector',
        // should just be one preview search here, still just mapping for type safety
        previews: previewSearches.map(search => ({
          search,
        })),
        highlights,
        onInspectSelector: setSelector,
      },
      fullScreen: false,
      onConfirm: () => {
        props.onConfirm(selector);
      },
      confirmLabel: intl.formatMessage({ defaultMessage: 'Apply', id: 'EWw/tK' }),
      emptyMessage: intl.formatMessage(
        {
          defaultMessage: 'Element Preview<b></b>Load preview to start defining your element',
          id: 'Qopba4',
        },
        {
          b: () => <br />,
        },
      ),
    },
  };
};
